import React, { FC, useCallback, useMemo } from 'react';
import { connect } from 'react-redux';
import { Redirect, Route, withRouter } from 'react-router';
import {
	PERMISSION_NAMES,
	isUserAllowedToAccess,
	isUserManagedBySupplier,
} from '../../utils/AuthUtils';
import OverviewShell from '../overview_shell/OverviewShell';
import { isRefrigerantTrackingEnabled } from '../../utils/FeatureFlagUtils';

require('./AssetsOverviewPage.less');

const AssetsOverviewPage: FC<any> = ({
	history,
	currentUser,
	companyConfig,
	childRoutes,
	userType,
}): React.ReactElement => {
	const handleTabChange = useCallback(
		(key) => history.push(`/${userType}/assets/overview/${key}`),
		[history, userType]
	);

	const checkTabActive = useCallback(
		(key) => {
			const pathname = history.location.pathname;
			return pathname.indexOf(key) !== -1;
		},
		[history.location.pathname]
	);

	const templatesVisible = useMemo(
		() =>
			isUserAllowedToAccess(currentUser.userType)(
				PERMISSION_NAMES.MODIFY_ASSET_TYPES,
				companyConfig,
				currentUser.roles
			),
		[companyConfig, currentUser.roles, currentUser.userType]
	);

	const isNotSupplierManaged = useMemo(() => !isUserManagedBySupplier(currentUser), []);

	const refrigerantTrackingEnabled = isRefrigerantTrackingEnabled();

	const tabs = useMemo(
		() => [
			{
				key: 'all',
				name: 'All',
			},
			...(templatesVisible && isNotSupplierManaged
				? [
						{
							key: 'templates/overview',
							name: 'Types',
						},
				  ]
				: []),
			...(isNotSupplierManaged
				? [
						{
							key: 'downtime',
							name: 'Downtime',
						},
				  ]
				: []),
			// Gated on the PostHog flag alone, with no per-buyer condition beneath it: there is no
			// "refrigerant tracking is on" field on `refrigerantConfig` to read — it holds regimes and
			// threshold overrides, both meaningful only once a buyer is already enrolled. Inside the
			// flag the tab lands on an empty state for a company with no tracked appliances, which is
			// the honest answer for them.
			//
			// `checkTabActive` is a substring match, and 'refrigerant' is a prefix of the Event Log
			// route too, so the tab stays lit across both sub-routes.
			...(refrigerantTrackingEnabled
				? [
						{
							key: 'refrigerant',
							name: 'Refrigerant Tracking',
						},
				  ]
				: []),
		],
		[templatesVisible, isNotSupplierManaged, refrigerantTrackingEnabled]
	);

	return (
		<>
			<Route
				path={`/${userType}/assets/overview`}
				exact={true}
				render={() => (
					<Redirect
						to={{
							pathname: `/${userType}/assets/overview/all`,
						}}
					/>
				)}
			/>
			<OverviewShell
				title="Assets"
				tabs={tabs}
				onTabChange={handleTabChange}
				checkTabActive={checkTabActive}
				childRoutes={childRoutes}
				className="assetsPage"
			/>
		</>
	);
};

const mapStateToProps = (state) => ({
	companyConfig: state.company_config.detail,
	currentUser: state.session.currentUser,
});

const ComponentWithoutUserType = withRouter(connect(mapStateToProps, null)(AssetsOverviewPage));

export default connect(
	(state) => ({
		userType: (state as any).session.userType,
	}),
	() => ({})
)(ComponentWithoutUserType);
